import React, { useState } from 'react';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Box, Badge, IconButton, Typography, useTheme } from '@mui/material';
import "react-pro-sidebar/dist/css/styles.css";
import { Link, useLocation } from 'react-router-dom';
import { tokens } from '../../../theme';
import useAuth from '../../../hooks/useAuth';
import {
  HomeOutlined,
  PeopleOutlined,
  DescriptionOutlined,
  CorporateFareOutlined,
  BusinessOutlined,
  DevicesOutlined,
  DataObjectOutlined,
  FactCheckOutlined,
  MenuOutlined,
  AdminPanelSettingsOutlined,
  MonitorHeartOutlined,
  ApiOutlined,
} from '@mui/icons-material';
import fivebyfive from '../../../assets/5x5coop.svg';

import { useLogActionMutation } from '../../../features/action_log/actionLogApiSlice';

const Item = ({ title, to, icon, selected, setSelected, username }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [logAction] = useLogActionMutation();
  const location = useLocation();   // gives us current pathname

  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100] }}
      onClick={() => {
        setSelected(title);
        logAction({
          action: "page_view",
          user: { username },
          details: {
            page: title,
            fromPath: location.pathname,
            toPath: to,
          },
        }).catch(() => { });
      }}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { username, role, status, organization_id } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  /* super-admins who can see the Admin Dashboard */
  const superAdmins = ["dallas5x5", "dallasmoto"];   // ← hard-coded

  return (
    <Box display="flex" sx={{
      "& .pro-sidebar-inner": {
        background: `${colors.primary[700]} !important`,
      },
      "& .pro-icon-wrapper": {
        backgroundColor: "transparent !important",
      },
      "& .pro-inner-item": {
        padding: "5px 35px 5px 20px !important",
      },
      "& .pro-inner-item:hover": {
        color: `${colors.greenAccent[400]} !important`,
      },
      "& .pro-menu-item.active": {
        color: `${colors.greenAccent[600]} !important`,
      },
      height: "100%"
    }}>
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlined /> : undefined}
            style={{ margin: "10px 0 20px 0", color: colors.grey[100] }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="45px">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={fivebyfive}
                  style={{ cursor: "pointer", borderRadius: "0%" }}
                />

                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlined />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <Typography variant="h4" color={colors.grey[200]}>
                  Data Dashboard
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  {username}
                </Typography>
                <Typography variant="h5" color={colors.blueAccent[300]}>
                  Partner Login - {role}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title="Dashboard"
              to="/console"
              icon={<HomeOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />
            <Item
              title="Dev Blog"
              to="/console/dev-blog"
              icon={<DescriptionOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />
            {!isCollapsed && (
              <Typography
                variant="h4"
                color={colors.primary[100]}
                sx={{ m: "15px 0 5px 20px" }}
              >
                Manage
              </Typography>
            )}
            {role === 'Admin' && (
              <Item
                title="Manage Organizations"
                to="/console/organizations"
                icon={<CorporateFareOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {role === 'Read-Only' && (
              <Item
                title="View Organizations"
                to="/console/organizations"
                icon={<CorporateFareOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {status === 'Manager' && (
              <Item
                title="Manage Organization"
                to={`/console/organization/${organization_id}`}
                icon={<BusinessOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {role === 'Admin' && (
              <Item
                title="Manage Team"
                to="/console/users"
                icon={<PeopleOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {role === 'Read-Only' && (
              <Item
                title="View Team"
                to="/console/users"
                icon={<PeopleOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {role === 'Admin' && (
              <Item
                title="Data QA"
                to="/console/data-qa"
                icon={<FactCheckOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
          </Box>

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            {!isCollapsed && (
              <Typography
                variant="h4"
                color={colors.primary[100]}
                sx={{ m: "15px 0 5px 20px" }}
              >
                Dashboards
              </Typography>
            )}
            <Item
              title="ADC"
              to="/console/media-adc"
              icon={<DevicesOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />

            <Item
              title={
                <Typography component="span">Market Pulse</Typography>
              }
              to="/console/market-pulse"
              icon={<MonitorHeartOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />
            <Item
              title="APIs"
              to="/console/media-apis"
              icon={<ApiOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />
            {/* <Item
              title="LDC"
              to="/console/ldc"
              icon={<CloudCircleOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            /> */}
            <Item
              title="Data Assets"
              to="/console/data"
              icon={<DataObjectOutlined />}
              selected={selected}
              setSelected={setSelected}
              username={username}
            />
            {superAdmins.includes(username.toLowerCase()) && (
              <Item
                title="Admin Feed"
                to="/console/admin"
                icon={<AdminPanelSettingsOutlined />}
                selected={selected}
                setSelected={setSelected}
                username={username}
              />
            )}
            {/* role === 'Admin' && (
              <Item
                title="Delivery Matrix"
                to="/console/delivery-matrix" // Placeholder URL
                icon={<MapOutlined />} // Random MUI icon
                selected={selected}
                setSelected={setSelected}
              />
            ) */}
            {/* <Item
              title="Coop Data"
              to="/console/coop-data"
              icon={<TimelineOutlined />}
              selected={selected}
              setSelected={setSelected}
            /> */}
          </Box>
        </Menu>
        <Box flexGrow={1}></Box>
        <Box display="flex" justifyContent="center" alignItems="center" mb="20px">
          <Typography variant="h4" color={colors.grey[200]}>
            Dropswitch - 2026
          </Typography>
        </Box>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
